/*
 * School Checkout (Phase D + E). Loads a quote and offers two payment paths:
 *  - Pay by card → Stripe Checkout (redirect)
 *  - Purchase Order → shows vendor package + AP secure link + inline PO upload
 * Also serves as the Stripe cancel redirect target.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { CreditCard, FileText, Loader2, Upload, Copy, Check, ShieldCheck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SchoolCheckoutPage() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState(null); // "card" | "po"
  const [redirecting, setRedirecting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [poNumber, setPoNumber] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [q, vp] = await Promise.all([
        axios.get(`${API}/api/school-onboarding/quote/${quoteId}`),
        axios.get(`${API}/api/school-onboarding/vendor-package`),
      ]);
      setQuote(q.data);
      setVendor(vp.data);
    } catch { toast.error("Could not load your quote."); } finally { setLoading(false); }
  }, [quoteId]);
  useEffect(() => { load(); }, [load]);

  const payByCard = async () => {
    setRedirecting(true);
    try {
      const r = await axios.post(`${API}/api/school-onboarding/checkout`, {
        quote_id: quoteId, origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setRedirecting(false);
    }
  };

  const apLink = quote?.ap_token ? `${window.location.origin}/ap/${quote.ap_token}` : "";
  const copyLink = () => { navigator.clipboard.writeText(apLink); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const uploadPO = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a PO file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("po_number", poNumber);
      fd.append("submitted_by_name", quote.purchaser_name || "");
      fd.append("submitted_by_email", quote.purchaser_email || "");
      await axios.post(`${API}/api/ap/${quote.ap_token}/purchase-order`, fd);
      toast.success("Purchase Order submitted — 589 Labs will review it shortly.");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); } finally { setUploading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6b7280]"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…</div>;
  if (!quote) return <div className="min-h-screen flex items-center justify-center text-[#6b7280]">Quote not found.</div>;

  const po = quote.purchase_order;
  const alreadyPaid = quote.payment_status === "paid" || quote.tenant_id;

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="school-checkout-page">
      <header className="sticky top-0 z-30 bg-white border-b border-[#e5e7eb]">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 flex items-center justify-between h-20">
          <button onClick={() => navigate("/myschoolrx")} className="flex items-center"><img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-10 w-auto" /></button>
          <a href="/diagnostic/login" className="text-[13px] font-semibold text-[#374151]">Sign in</a>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
        <h1 className="text-2xl font-bold text-[#111827]">Checkout</h1>
        <div className="mt-4 rounded-xl border border-[#e5e7eb] bg-white p-5" data-testid="checkout-quote-summary">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[15px] font-bold text-[#111827]">{quote.school_name}</div>
              <div className="text-[13px] text-[#6b7280]">Quote {quote.quote_number}</div>
            </div>
            <div className="text-2xl font-bold text-[#111827]" data-testid="checkout-total">${Number(quote.annual_total).toLocaleString()}</div>
          </div>
        </div>

        {alreadyPaid ? (
          <div className="mt-6 rounded-xl border border-[#a7f3d0] bg-[#ecfdf5] p-5 text-center" data-testid="checkout-already-paid">
            <ShieldCheck className="h-8 w-8 text-[#059669] mx-auto" />
            <p className="mt-2 text-[15px] font-semibold text-[#065f46]">This quote is already {quote.payment_status === "paid" ? "paid" : "in progress"}.</p>
            <Button onClick={() => navigate("/diagnostic/login")} className="mt-4 bg-[#059669] hover:bg-[#047857] text-white">Sign in to your workspace</Button>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button onClick={() => setMethod("card")} className={`rounded-xl border p-5 text-left ${method === "card" ? "border-[#007a9e] bg-[#f0f9fc]" : "border-[#e5e7eb] hover:border-[#007a9e]/40"}`} data-testid="checkout-method-card">
                <CreditCard className="h-6 w-6 text-[#007a9e]" />
                <div className="mt-2 text-[15px] font-bold text-[#111827]">Pay by card</div>
                <div className="text-[13px] text-[#6b7280]">Secure Stripe checkout. Workspace activates instantly.</div>
              </button>
              <button onClick={() => setMethod("po")} className={`rounded-xl border p-5 text-left ${method === "po" ? "border-[#007a9e] bg-[#f0f9fc]" : "border-[#e5e7eb] hover:border-[#007a9e]/40"}`} data-testid="checkout-method-po">
                <FileText className="h-6 w-6 text-[#007a9e]" />
                <div className="mt-2 text-[15px] font-bold text-[#111827]">Purchase Order</div>
                <div className="text-[13px] text-[#6b7280]">Upload a signed PO. We'll invoice and activate on acceptance.</div>
              </button>
            </div>

            {method === "card" && (
              <div className="mt-5 rounded-xl border border-[#e5e7eb] bg-white p-5" data-testid="checkout-card-panel">
                <Button disabled={redirecting} onClick={payByCard} className="w-full h-12 bg-[#007a9e] hover:bg-[#00607d] text-white text-[15px] font-semibold disabled:opacity-50" data-testid="checkout-pay-card-btn">
                  {redirecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Redirecting to Stripe…</> : `Pay $${Number(quote.annual_total).toLocaleString()} by card`}
                </Button>
              </div>
            )}

            {method === "po" && (
              <div className="mt-5 rounded-xl border border-[#e5e7eb] bg-white p-5 space-y-5" data-testid="checkout-po-panel">
                {vendor && (
                  <div className="rounded-lg bg-[#f9fafb] border border-[#e5e7eb] p-4" data-testid="checkout-vendor-package">
                    <div className="text-[12px] font-semibold uppercase tracking-wide text-[#6b7280] mb-2">Vendor Package</div>
                    <div className="text-[13px] text-[#374151] space-y-1">
                      <div><span className="font-semibold">Vendor:</span> {vendor.vendor_legal_name}</div>
                      <div><span className="font-semibold">Terms:</span> {vendor.payment_terms}</div>
                      <div><span className="font-semibold">Remittance:</span> {vendor.remittance_email}</div>
                    </div>
                    <p className="mt-2 text-[12px] text-[#6b7280]">{vendor.instructions}</p>
                  </div>
                )}

                {apLink && (
                  <div>
                    <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">Secure AP link (share with your business office)</label>
                    <div className="flex gap-2">
                      <Input readOnly value={apLink} className="h-10 bg-white text-[13px]" data-testid="checkout-ap-link" />
                      <Button variant="outline" onClick={copyLink} data-testid="checkout-copy-ap-link">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
                    </div>
                    <p className="mt-1 text-[11px] text-[#9ca3af]">Your AP contact can open this link to view the vendor package and upload the PO — no login needed.</p>
                  </div>
                )}

                {po && po.status ? (
                  <div className="rounded-lg border border-[#bfdbfe] bg-[#eff6ff] p-4 text-[13px] text-[#1e40af]" data-testid="checkout-po-status">
                    Purchase Order <span className="font-semibold">{po.po_number || "(no number)"}</span> — status: <span className="font-semibold capitalize">{po.status.replace("_", " ")}</span>.
                  </div>
                ) : (
                  <div>
                    <div className="text-[12px] font-semibold uppercase tracking-wide text-[#6b7280] mb-2">Upload PO now (optional)</div>
                    <Input placeholder="PO number (optional)" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} className="h-10 mb-2" data-testid="checkout-po-number" />
                    <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx" className="block w-full text-[13px]" data-testid="checkout-po-file" />
                    <Button disabled={uploading} onClick={uploadPO} className="mt-3 bg-[#007a9e] hover:bg-[#00607d] text-white disabled:opacity-50" data-testid="checkout-po-upload-btn">
                      {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading…</> : <><Upload className="h-4 w-4 mr-2" />Submit Purchase Order</>}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
